import { AuthClientTokens } from '@react-keycloak/core';
import { RootState } from 'app/store';
import { STATE_VERSION } from 'common/constants';

enum LocalStorageKeys {
  KcTokens = 'kcTokens',
  StateVersion = 'version',
  State = 'state',
  CookieConsent = 'cookieConsent',
}

type OptionalRootState = Partial<RootState>;

class LocalStorageServiceImpl {
  private _writeData<T>(key: LocalStorageKeys, data: T) {
    try {
      const serialisedSlice = JSON.stringify(data);
      localStorage.setItem(key, serialisedSlice);
    } catch (err) {
      console.error(err);
    }
  }

  private _getData<T>(key: LocalStorageKeys): T | undefined {
    try {
      const serialisedSlice = localStorage.getItem(key);
      if (serialisedSlice === null) {
        return undefined;
      }
      return JSON.parse(serialisedSlice);
    } catch (err) {
      console.log(err);
      return undefined;
    }
  }

  private _getAllData() {
    return Object.fromEntries(
      Object.entries(localStorage).map((pair) => {
        try {
          return [pair[0], JSON.parse(pair[1])];
        } catch {
          return [pair[0], undefined];
        }
      }),
    );
  }

  private _removeData(key: LocalStorageKeys) {
    localStorage.removeItem(key);
  }

  // Cookie Consent Storage
  cookiesConsented() {
    this._writeData(LocalStorageKeys.CookieConsent, 'true');
  }

  getHasConsented() {
    const hasConsented = this._getData<string>(LocalStorageKeys.CookieConsent);
    if (hasConsented !== undefined) {
      return true;
    } else {
      return false;
    }
  }

  // Keycloak related storage functions
  saveKeycloakTokens(tokens: AuthClientTokens) {
    this._writeData<typeof tokens>(LocalStorageKeys.KcTokens, tokens);
  }

  getKeycloakTokens() {
    return this._getData<AuthClientTokens>(LocalStorageKeys.KcTokens);
  }

  removeKeycloakTokens() {
    this._removeData(LocalStorageKeys.KcTokens);
  }

  saveState(state: OptionalRootState) {
    this._writeData(LocalStorageKeys.State, state);
  }

  saveVersion() {
    this._writeData(LocalStorageKeys.StateVersion, STATE_VERSION);
  }

  private _migrate(savedState: any): OptionalRootState | undefined {
    const version = this._getData<string | undefined>(LocalStorageKeys.StateVersion);
    try {
      switch (version) {
        case undefined:
          // Update from undefined -> 1.0
          console.log(`Attempting to migrate previous state`);
          const preloadedState = {
            searchMolecules: {
              searchResults: savedState.results,
              isQueryLoading: false,
              selectedResultNum: savedState.results.length >= 0 ? 0 : undefined,
            },
            shoppingCart: savedState.shoppingCart,
            settings: savedState.settings,
          };
          localStorage.removeItem('results');
          localStorage.removeItem('shoppingCart');
          localStorage.removeItem('workingQuery');
          localStorage.removeItem('settings');
          return preloadedState;
        case STATE_VERSION:
          console.log(`Loading previous state from localStorage`);
          return this._getData<OptionalRootState>(LocalStorageKeys.State);
      }
    } catch (err) {
      console.error(err);
      console.log(`Couldn't migrate state from ${version} to ${STATE_VERSION}`);
      return undefined;
    }
  }

  loadState(): OptionalRootState | undefined {
    return this._migrate(this._getAllData());
  }

  removeState() {
    this._removeData(LocalStorageKeys.State);
  }
}

const LocalStorageService = new LocalStorageServiceImpl();
export default LocalStorageService;
